import { useQuery, UseQueryResult } from 'react-query';
import { axiosClient } from '@/services/utilities';

interface User {
  username: string;
  email: string;
  fullname: string;
}

interface UseUsersForRecipientParams {
  page: number;
  size: number;
  search?: string;
  enabled?: boolean;
}

interface UseUsersForRecipientResult {
  users: User[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: UseQueryResult["refetch"];
}

export const useUsersForRecipient = ({
  page,
  size,
  search = '',
  enabled = true,
}: UseUsersForRecipientParams): UseUsersForRecipientResult => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['usersForRecipient', page, size, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
      });

      if (search) {
        params.append('username', search);
      }

      const response = await axiosClient.get(`/users?${params.toString()}`);
      
      // Handle response structure
      const responseData = response.data?.data || response.data;
      
      return {
        data: Array.isArray(responseData) ? responseData : responseData?.data || [],
        total: response.data?.total || responseData?.total || 0,
      };
    },
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 30 * 1000, // 30 seconds stale time
    cacheTime: 5 * 60 * 1000, // 5 minutes cache time
  });

  return {
    users: data?.data || [],
    total: data?.total || 0,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
};

export default useUsersForRecipient;
